export const RADAR_LEVELS = [100, 75, 50, 25] as const;

export const RADAR_AXES = [
  {
    code: "control" as const,
    angle: -90,
    labelAnchor: "middle" as const,
    labelDy: -16,
    labelDx: 0,
  },
  {
    code: "growth" as const,
    angle: 30,
    labelAnchor: "start" as const,
    labelDy: 20,
    labelDx: 8,
  },
  {
    code: "safety" as const,
    angle: 150,
    labelAnchor: "end" as const,
    labelDy: 20,
    labelDx: -8,
  },
];

export const RADAR_GEOMETRY = {
  cx: 150,
  cy: 150,
  maxR: 112,
  /** Extra top room for Control label (Figma label sits above rings). */
  viewBox: "0 -20 300 320",
} as const;

/** Gap from axis intersection (center dot) to score badge bottom edge. */
export const RADAR_CENTER_BADGE_GAP = 4;

/** Figma-aligned axis label anchors (305×262 chart frame). */
export const RADAR_LABEL_POSITIONS = {
  control: { x: 150, y: 10, anchor: "middle" as const },
  growth: { x: 259, y: 199, anchor: "start" as const },
  safety: { x: 41, y: 199, anchor: "end" as const },
} as const;

/** Header score chip micro radar — 32×32 frame (Main Tab Header | Score Chip). */
export const RADAR_CHIP_GEOMETRY = {
  cx: 16,
  cy: 16,
  maxR: 12,
  viewBox: "0 0 32 32",
} as const;

/** Plan impact strip radar — Figma 97986:229026 · 142×114 frame. */
export const RADAR_PLAN_IMPACT_GEOMETRY = {
  cx: 71,
  /** Centroid of label triangle (y≈70), clamped to fit maxR within 114px frame. */
  cy: 66,
  maxR: 48,
  viewBox: "0 0 142 114",
} as const;

/** Growth top · Safety bottom-left · Control bottom-right (plan-preview layout). */
export const RADAR_PLAN_IMPACT_LABEL_POSITIONS = {
  growth: { x: 71, y: 10, anchor: "middle" as const },
  safety: { x: 22, y: 100, anchor: "end" as const },
  control: { x: 120, y: 100, anchor: "start" as const },
} as const;

/** Plan-impact axes — Growth at top (differs from onboarding-result Control-top). */
export const RADAR_PLAN_IMPACT_AXES = [
  {
    code: "growth" as const,
    angle: -90,
    labelAnchor: "middle" as const,
    labelDy: -16,
    labelDx: 0,
  },
  {
    code: "control" as const,
    angle: 30,
    labelAnchor: "start" as const,
    labelDy: 20,
    labelDx: 8,
  },
  {
    code: "safety" as const,
    angle: 150,
    labelAnchor: "end" as const,
    labelDy: 20,
    labelDx: -8,
  },
] as const;

/** Gentler curve tuning for 142×114 plan-impact frame. */
export const RADAR_PLAN_IMPACT_CURVE_OPTIONS = {
  vertexRoundRatio: 0.09,
  edgeInwardPull: 0.22,
  vertexBulgeRatio: 0.28,
} as const;

/** Trim ratio along each edge — leaves room for convex rounded tips. */
export const RADAR_VERTEX_ROUND_RATIO = 0.09;

/** Edge midpoint pull toward center (concave / 中间凹). */
export const RADAR_EDGE_INWARD_PULL = 0.58;

/** Outward bulge at each vertex tip (convex / 两边凸). */
export const RADAR_VERTEX_BULGE_RATIO = 0.42;

export function polarToCartesian(
  cx: number,
  cy: number,
  angleDeg: number,
  radius: number,
): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
}

type RadarAxisCode = (typeof RADAR_AXES)[number]["code"];

type RadarAxis = {
  code: RadarAxisCode;
  angle: number;
};

export function buildRadarDataVertices(
  scores: { control: number; safety: number; growth: number },
  maxR: number,
  cx: number,
  cy: number,
  progress = 1,
  axes: ReadonlyArray<RadarAxis> = RADAR_AXES,
): Array<[number, number]> {
  const axisValues: Record<RadarAxisCode, number> = {
    control: scores.control,
    safety: scores.safety,
    growth: scores.growth,
  };

  return axes.map((axis) => {
    const value = (axisValues[axis.code] / 100) * maxR * progress;
    return polarToCartesian(cx, cy, axis.angle, value);
  });
}

function formatPoint([x, y]: [number, number]): string {
  return `${x.toFixed(2)} ${y.toFixed(2)}`;
}

function lerpPoint(
  from: [number, number],
  to: [number, number],
  t: number,
): [number, number] {
  return [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
}

function distance(a: [number, number], b: [number, number]): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

function unitFromCenter(
  point: [number, number],
  cx: number,
  cy: number,
): [number, number] {
  const len = Math.hypot(point[0] - cx, point[1] - cy) || 1;
  return [(point[0] - cx) / len, (point[1] - cy) / len];
}

/**
 * Figma DataShape curve: each edge is concave toward center (中间凹),
 * each vertex bulges outward with a rounded tip (两边凸 + 圆角).
 *
 * Per edge Vi → V{i+1}:
 * 1. Quadratic inward: leave(Vi) → approach(V{i+1}), ctrl pulled to center
 * 2. Quadratic outward: approach(V{i+1}) → leave(V{i+1}), ctrl beyond vertex
 */
export function buildRadarCurvePath(
  vertices: Array<[number, number]>,
  cx: number,
  cy: number,
  options?: {
    vertexRoundRatio?: number;
    edgeInwardPull?: number;
    vertexBulgeRatio?: number;
  },
): string {
  const n = vertices.length;
  if (n < 3) {
    return "";
  }

  const vertexRoundRatio =
    options?.vertexRoundRatio ?? RADAR_VERTEX_ROUND_RATIO;
  const edgeInwardPull = options?.edgeInwardPull ?? RADAR_EDGE_INWARD_PULL;
  const vertexBulgeRatio =
    options?.vertexBulgeRatio ?? RADAR_VERTEX_BULGE_RATIO;

  const getVertex = (index: number): [number, number] =>
    vertices[(index + n) % n];

  let path = "";

  for (let i = 0; i < n; i += 1) {
    const curr = getVertex(i);
    const next = getVertex(i + 1);
    const afterNext = getVertex(i + 2);

    const leave = lerpPoint(curr, next, vertexRoundRatio);
    const approachNext = lerpPoint(curr, next, 1 - vertexRoundRatio);

    if (i === 0) {
      path = `M ${formatPoint(leave)}`;
    }

    const edgeMid: [number, number] = [
      (leave[0] + approachNext[0]) / 2,
      (leave[1] + approachNext[1]) / 2,
    ];
    const inwardCtrl: [number, number] = [
      edgeMid[0] + (cx - edgeMid[0]) * edgeInwardPull,
      edgeMid[1] + (cy - edgeMid[1]) * edgeInwardPull,
    ];
    path += ` Q ${formatPoint(inwardCtrl)} ${formatPoint(approachNext)}`;

    const outward = unitFromCenter(next, cx, cy);
    const tipSpan = distance(approachNext, next);
    const cornerCtrl: [number, number] = [
      next[0] + outward[0] * tipSpan * vertexBulgeRatio,
      next[1] + outward[1] * tipSpan * vertexBulgeRatio,
    ];
    const leaveNext = lerpPoint(next, afterNext, vertexRoundRatio);
    path += ` Q ${formatPoint(cornerCtrl)} ${formatPoint(leaveNext)}`;
  }

  return `${path} Z`;
}

/** @deprecated Prefer buildRadarDataVertices + buildRadarCurvePath */
export function buildRadarPolygonPoints(
  scores: { control: number; safety: number; growth: number },
  maxR: number,
  cx: number,
  cy: number,
  progress = 1,
): string {
  return buildRadarDataVertices(scores, maxR, cx, cy, progress)
    .map(([x, y]) => `${x},${y}`)
    .join(" ");
}
